/**
 * Laydown Reconciliation — Sprint 0072
 *
 * Closes the cotton mass balance: cotton issued at laydown vs cotton
 * consumed by the cards (sliver out + blow-room waste + card waste).
 * The supervisor's "Complete & Confirm Net" click only marks the bales
 * SCANNED at the feed point; this page is where they actually clear
 * once the cards have chewed through them.
 *
 * Verdict bands:
 *   AUTO_CLOSED            green     ≤ 0.5 % variance, no flag
 *   FLAGGED_MINOR          orange    ≤ 1.5 %, auto-closes but visible
 *   IN_PROGRESS            blue      cards still running, will close later
 *   PENDING_INVESTIGATION  red       > 1.5 % over-consumption — review
 *   MANUAL_CLOSED          grey      supervisor force-closed with reason
 *   GRANDFATHERED          slate     legacy row pre-0072
 */
import { useState } from 'react'
import {
  App, Alert, Button, Card, Col, Empty, Input, Modal, Progress, Row,
  Select, Space, Statistic, Table, Tag, Tooltip, Typography,
} from 'antd'
import {
  ReloadOutlined, WarningOutlined, CheckCircleOutlined, CloseCircleOutlined,
  ClockCircleOutlined, LockOutlined,
} from '@ant-design/icons'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { reconciliationApi } from '@/features/baleManagement/api'
import { plantsApi } from '@/features/masters/plants/api'
import PageHeader from '@/shared/components/PageHeader'

const { Text } = Typography

interface PendingRow {
  id: string
  laydown_no: string
  count_description: string | null
  plant_id: string
  status: string
  reconciliation_status: string
  reconciliation_mode: 'FIFO' | 'EXPLICIT'
  mixing_started_at: string | null
  mixing_completed_at: string | null
  issued_kg: number
  consumed_kg: number
  variance_kg: number
  variance_pct: number
  progress_pct: number
}

const STATUS_TAG: Record<
  string, { color: string; label: string; icon: React.ReactNode }
> = {
  NOT_STARTED:           { color: 'default', label: 'Awaiting first card output', icon: <ClockCircleOutlined /> },
  IN_PROGRESS:           { color: 'blue',    label: 'Cards consuming…',           icon: <ClockCircleOutlined /> },
  AUTO_CLOSED:           { color: 'green',   label: 'Auto-closed — clean',        icon: <CheckCircleOutlined /> },
  FLAGGED_MINOR:         { color: 'orange',  label: 'Closed — minor variance',    icon: <WarningOutlined /> },
  PENDING_INVESTIGATION: { color: 'red',     label: 'Investigate (>1.5%)',         icon: <CloseCircleOutlined /> },
  MANUAL_CLOSED:         { color: 'purple',  label: 'Manually closed',             icon: <LockOutlined /> },
  GRANDFATHERED:         { color: 'default', label: 'Legacy',                      icon: <LockOutlined /> },
}

export default function LaydownReconciliationPage() {
  const { message } = App.useApp()
  const qc = useQueryClient()
  const [plantFilter, setPlantFilter] = useState<string | undefined>()
  const [forceCloseRow, setForceCloseRow] = useState<PendingRow | null>(null)
  const [forceCloseNote, setForceCloseNote] = useState('')

  const { data: plantsData } = useQuery({
    queryKey: ['plants-all'],
    queryFn: () => plantsApi.list({ page: 1, page_size: 500 }),
  })
  const plantOptions = plantsData?.items?.map((p: any) => ({ label: p.name, value: p.id })) ?? []

  const { data: rows = [], isLoading, refetch } = useQuery<PendingRow[]>({
    queryKey: ['reconciliation-pending', plantFilter],
    queryFn: () => reconciliationApi.pending(plantFilter),
    refetchInterval: 60_000, // every minute — production logs come in every 6h
  })

  const runMutation = useMutation({
    mutationFn: (plant_id: string) => reconciliationApi.run(plant_id),
    onSuccess: (res: any) => {
      message.success(`Reconciliation pass complete — ${res.processed} laydown(s) refreshed`)
      qc.invalidateQueries({ queryKey: ['reconciliation-pending'] })
    },
    onError: (e: any) => message.error(e.response?.data?.detail || 'Failed'),
  })

  const forceCloseMutation = useMutation({
    mutationFn: (vars: { id: string; note: string }) =>
      reconciliationApi.forceClose(vars.id, vars.note),
    onSuccess: () => {
      message.success('Laydown force-closed and audit logged')
      setForceCloseRow(null)
      setForceCloseNote('')
      qc.invalidateQueries({ queryKey: ['reconciliation-pending'] })
    },
    onError: (e: any) => message.error(e.response?.data?.detail || 'Failed'),
  })

  // Top-of-page KPI: counts in each band.
  const inProgress = rows.filter(r => ['NOT_STARTED', 'IN_PROGRESS'].includes(r.reconciliation_status)).length
  const flagged = rows.filter(r => r.reconciliation_status === 'FLAGGED_MINOR').length
  const investigate = rows.filter(r => r.reconciliation_status === 'PENDING_INVESTIGATION').length
  const totalIssued = rows.reduce((s, r) => s + (r.issued_kg || 0), 0)
  const totalConsumed = rows.reduce((s, r) => s + (r.consumed_kg || 0), 0)

  const columns = [
    {
      title: 'Laydown', dataIndex: 'laydown_no', width: 130,
      fixed: 'left' as const,
      render: (v: string, r: PendingRow) => (
        <Space direction="vertical" size={0}>
          <Text code strong>{v}</Text>
          {r.count_description && (
            <Text type="secondary" style={{ fontSize: 10 }}>
              {r.count_description}
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: 'Mode', dataIndex: 'reconciliation_mode', width: 90,
      render: (v: string) => (
        <Tooltip title={v === 'FIFO'
          ? 'FIFO — system attributes Production Log rows automatically (oldest pending laydown first).'
          : 'EXPLICIT — operator tags each Production Log row with its target laydown.'}>
          <Tag>{v}</Tag>
        </Tooltip>
      ),
    },
    {
      title: 'Mixed', dataIndex: 'mixing_completed_at', width: 130,
      render: (v: string | null) => v ? dayjs(v).format('DD-MMM HH:mm') : '—',
    },
    {
      title: 'Issued (kg)', dataIndex: 'issued_kg', width: 110, align: 'right' as const,
      render: (v: number) => <Text strong style={{ fontVariantNumeric: 'tabular-nums' }}>
        {v.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 3 })}
      </Text>,
    },
    {
      title: 'Consumed (kg)', dataIndex: 'consumed_kg', width: 130, align: 'right' as const,
      render: (v: number) => <Text style={{ fontVariantNumeric: 'tabular-nums' }}>
        {v.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 3 })}
      </Text>,
    },
    {
      title: 'Progress', dataIndex: 'progress_pct', width: 200,
      render: (v: number, r: PendingRow) => (
        <Progress
          percent={Math.min(100, Math.round(v))}
          size="small"
          status={
            r.reconciliation_status === 'PENDING_INVESTIGATION' ? 'exception' :
            ['AUTO_CLOSED', 'FLAGGED_MINOR'].includes(r.reconciliation_status) ? 'success' :
            'active'
          }
        />
      ),
    },
    {
      title: 'Variance', dataIndex: 'variance_pct', width: 110, align: 'right' as const,
      render: (pct: number, r: PendingRow) => {
        const color = pct <= 0.5 ? '#52c41a' : pct <= 1.5 ? '#faad14' : '#ff4d4f'
        return (
          <Space direction="vertical" size={0} style={{ width: '100%', textAlign: 'right' }}>
            <Text strong style={{ color, fontVariantNumeric: 'tabular-nums' }}>
              {pct.toFixed(2)}%
            </Text>
            <Text type="secondary" style={{ fontSize: 10, fontVariantNumeric: 'tabular-nums' }}>
              {r.variance_kg >= 0 ? '+' : ''}{r.variance_kg.toFixed(1)} kg
            </Text>
          </Space>
        )
      },
    },
    {
      title: 'Status', dataIndex: 'reconciliation_status', width: 200,
      render: (v: string) => {
        const cfg = STATUS_TAG[v] ?? { color: 'default', label: v, icon: null }
        return <Tag color={cfg.color} icon={cfg.icon}>{cfg.label}</Tag>
      },
    },
    {
      title: 'Action', key: 'action', width: 130, fixed: 'right' as const,
      render: (_: any, r: PendingRow) => {
        const canForceClose = ['IN_PROGRESS', 'PENDING_INVESTIGATION'].includes(r.reconciliation_status)
        return canForceClose ? (
          <Button
            size="small"
            danger={r.reconciliation_status === 'PENDING_INVESTIGATION'}
            onClick={() => { setForceCloseRow(r); setForceCloseNote('') }}
          >
            Force close
          </Button>
        ) : null
      },
    },
  ]

  return (
    <div>
      <PageHeader
        title="Laydown Reconciliation"
        subtitle="Cotton issued at laydown vs cotton consumed by cards — closes the mass balance every 6 hours as Production Logs come in"
        extra={
          <Space>
            <Select
              allowClear placeholder="Plant"
              options={plantOptions}
              onChange={v => setPlantFilter(v)}
              style={{ width: 180 }}
            />
            <Button
              icon={<ReloadOutlined />}
              onClick={() => refetch()}
            >
              Refresh
            </Button>
            <Button
              type="primary"
              icon={<ReloadOutlined />}
              loading={runMutation.isPending}
              disabled={!plantFilter}
              onClick={() => plantFilter && runMutation.mutate(plantFilter)}
            >
              Run reconciliation now
            </Button>
          </Space>
        }
      />

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="How this works"
        description={
          <span style={{ fontSize: 12 }}>
            Each Production Log row from the cards (sliver out + waste) and blow room (waste) is
            attributed to a laydown — automatically by FIFO, or explicitly by operator tag. When
            <b> issued ≈ consumed within ±0.5%</b>, the laydown auto-closes (status = MIXING_CONSUMED).
            Variance ≤ 1.5% closes with a flag. Variance over 1.5% (over-consumption) stays open for
            investigation. Cards still running = IN_PROGRESS, no flag.
          </span>
        }
      />

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic title="In progress" value={inProgress}
              valueStyle={{ color: '#1677ff' }} prefix={<ClockCircleOutlined />} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic title="Flagged minor" value={flagged}
              valueStyle={{ color: '#faad14' }} prefix={<WarningOutlined />} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic title="Investigate" value={investigate}
              valueStyle={{ color: '#ff4d4f' }} prefix={<CloseCircleOutlined />} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic title="Issued vs Consumed"
              value={`${totalConsumed.toFixed(0)} / ${totalIssued.toFixed(0)} kg`}
              valueStyle={{ fontSize: 16 }} />
          </Card>
        </Col>
      </Row>

      <Card>
        {rows.length === 0 && !isLoading ? (
          <Empty description="No laydowns pending reconciliation. All caught up." />
        ) : (
          <Table
            rowKey="id"
            columns={columns}
            dataSource={rows}
            loading={isLoading}
            size="small"
            pagination={{ pageSize: 50 }}
            scroll={{ x: 'max-content' }}
            rowClassName={(r: PendingRow) =>
              r.reconciliation_status === 'PENDING_INVESTIGATION' ? 'ant-table-row-danger' :
              r.reconciliation_status === 'FLAGGED_MINOR' ? 'ant-table-row-warning' : ''
            }
          />
        )}
      </Card>

      {/* Force-close modal — supervisor must enter a reason for audit. */}
      <Modal
        title={
          <Space>
            <LockOutlined style={{ color: '#fa541c' }} />
            <span>Force-close laydown</span>
          </Space>
        }
        open={!!forceCloseRow}
        onCancel={() => setForceCloseRow(null)}
        onOk={() => {
          if (forceCloseRow && forceCloseNote.trim().length >= 5) {
            forceCloseMutation.mutate({ id: forceCloseRow.id, note: forceCloseNote.trim() })
          } else {
            message.error('Provide a reason (≥5 chars) before force-closing')
          }
        }}
        confirmLoading={forceCloseMutation.isPending}
        okText="Confirm force-close"
        okButtonProps={{ danger: true }}
      >
        {forceCloseRow && (
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <Alert
              type="warning"
              showIcon
              message={`Closing ${forceCloseRow.laydown_no} with ${forceCloseRow.variance_pct.toFixed(2)}% variance`}
              description={`Issued ${forceCloseRow.issued_kg.toFixed(1)} kg · Consumed ${forceCloseRow.consumed_kg.toFixed(1)} kg · Gap ${forceCloseRow.variance_kg.toFixed(1)} kg.`}
            />
            <div>
              <Text strong>Reason</Text> (logged for audit — required)
              <Input.TextArea
                rows={3}
                value={forceCloseNote}
                onChange={e => setForceCloseNote(e.target.value)}
                placeholder="e.g. Card waste under-logged on shift A 09-May; physical stock verified by QC."
                maxLength={500}
              />
            </div>
          </Space>
        )}
      </Modal>
    </div>
  )
}
